import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, ArrowUp, ArrowDown, Filter, FileText, Upload, CheckCircle, Download, Star, Layers } from 'lucide-react';
import { showGlobalNotification } from '../hooks/useNotifications';
import { useLanguage } from '../contexts/LanguageContext';

interface AssignmentDefinition {
  id: number;
  title: string;
  content: string;
  category_id: number;
  category_name?: string;
  category_icon?: string;
  category_color?: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ZimmetTemplate {
  id: number;
  name: string;
  description?: string;
  template_key: string;
  file_path?: string;
  file_original_name?: string;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
}

interface AssignmentCategory {
  id: number;
  name: string;
  description: string;
  icon: string;
  color: string;
  sort_order: number;
  is_active: boolean;
  definition_count: number;
}

const AssignmentDefinitionsPage: React.FC = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'definitions' | 'templates'>('definitions');
  const [definitions, setDefinitions] = useState<AssignmentDefinition[]>([]);
  const [categories, setCategories] = useState<AssignmentCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingDefinition, setEditingDefinition] = useState<Partial<AssignmentDefinition>>({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [newDefinition, setNewDefinition] = useState({ title: '', content: '', category_id: 0, sort_order: 0 });
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<number | null>(null);

  // Template states
  const [templates, setTemplates] = useState<ZimmetTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '', template_key: '' });
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const [templateSaving, setTemplateSaving] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadDefinitions();
  }, [selectedCategoryFilter]);

  useEffect(() => {
    if (activeTab === 'templates') loadTemplates();
  }, [activeTab]);

  const loadCategories = async () => {
    try {
      const response = await fetch('/api/assignment-categories/all', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data.data || []);
      }
    } catch (error: any) {
      console.error('Kategoriler yükleme hatası:', error);
    }
  };

  const loadDefinitions = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/assignment-definitions/all', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDefinitions(data.data || []);
      } else {
        throw new Error('Tanımlar yüklenemedi');
      }
    } catch (error: any) {
      console.error('Tanımlar yükleme hatası:', error);
      showGlobalNotification.error('Hata', error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const res = await fetch('/api/zimmet-templates', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.data || []);
      }
    } catch (error) {
      console.error('Şablon yükleme hatası:', error);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleActivateTemplate = async (id: number) => {
    try {
      const res = await fetch(`/api/zimmet-templates/${id}/activate`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (res.ok) {
        showGlobalNotification.success(t('zimmetDef.success'), t('zimmetDef.successActivate'));
        loadTemplates();
      } else {
        throw new Error(t('zimmetDef.errActivate'));
      }
    } catch (error: any) {
      showGlobalNotification.error('Hata', error.message);
    }
  };

  const handleDeleteTemplate = async (id: number) => {
    if (!confirm(t('zimmetDef.confirmDeleteTemplate'))) return;
    try {
      const res = await fetch(`/api/zimmet-templates/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await res.json();
      if (res.ok) {
        showGlobalNotification.success(t('zimmetDef.success'), t('zimmetDef.successDeleteTemplate'));
        loadTemplates();
      } else {
        throw new Error(data.error || t('zimmetDef.errActivate'));
      }
    } catch (error: any) {
      showGlobalNotification.error('Hata', error.message);
    }
  };

  const handleDownloadTemplate = (id: number) => {
    window.open(`/api/zimmet-templates/${id}/download`, '_blank');
  };

  const handleAddTemplate = async () => {
    if (!templateForm.name || !templateForm.template_key) {
      showGlobalNotification.error(t('zimmetDef.error'), t('zimmetDef.errTemplateFields'));
      return;
    }
    try {
      setTemplateSaving(true);
      const formData = new FormData();
      formData.append('name', templateForm.name);
      formData.append('description', templateForm.description);
      formData.append('template_key', templateForm.template_key);
      if (templateFile) formData.append('file', templateFile);

      const res = await fetch('/api/zimmet-templates', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
        body: formData
      });
      const data = await res.json();
      if (res.ok) {
        showGlobalNotification.success(t('zimmetDef.success'), t('zimmetDef.successAddTemplate'));
        setShowTemplateModal(false);
        setTemplateForm({ name: '', description: '', template_key: '' });
        setTemplateFile(null);
        loadTemplates();
      } else {
        throw new Error(data.error || t('zimmetDef.errActivate'));
      }
    } catch (error: any) {
      showGlobalNotification.error('Hata', error.message);
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleEdit = (definition: AssignmentDefinition) => {
    setEditingId(definition.id);
    setEditingDefinition({ ...definition });
  };

  const handleSave = async () => {
    try {
      const response = await fetch(`/api/assignment-definitions/${editingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(editingDefinition)
      });

      if (response.ok) {
        showGlobalNotification.success(t('zimmetDef.success'), t('zimmetDef.successUpdate'));
        setEditingId(null);
        setEditingDefinition({});
        loadDefinitions();
      } else {
        throw new Error(t('zimmetDef.errUpdate'));
      }
    } catch (error: any) {
      showGlobalNotification.error('Hata', error.message);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditingDefinition({});
  };

  const handleDelete = async (id: number) => {
    if (!confirm(t('zimmetDef.confirmDelete'))) {
      return;
    }

    try {
      const response = await fetch(`/api/assignment-definitions/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        showGlobalNotification.success(t('zimmetDef.success'), t('zimmetDef.successDelete'));
        loadDefinitions();
      } else {
        throw new Error(t('zimmetDef.errUpdate'));
      }
    } catch (error: any) {
      showGlobalNotification.error('Hata', error.message);
    }
  };

  const handleAdd = async () => {
    if (!newDefinition.title || !newDefinition.content) {
      showGlobalNotification.error(t('zimmetDef.error'), t('zimmetDef.errTitleContent'));
      return;
    }

    if (!newDefinition.category_id || newDefinition.category_id === 0) {
      showGlobalNotification.error(t('zimmetDef.error'), t('zimmetDef.errCategory'));
      return;
    }

    try {
      const response = await fetch('/api/assignment-definitions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          title: newDefinition.title,
          content: newDefinition.content,
          category_id: Number(newDefinition.category_id),
          sort_order: newDefinition.sort_order || 0,
          is_active: true
        })
      });

      if (response.ok) {
        showGlobalNotification.success(t('zimmetDef.success'), t('zimmetDef.successAdd'));
        setNewDefinition({ title: '', content: '', category_id: 0, sort_order: 0 });
        setShowAddModal(false);
        loadDefinitions();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || t('zimmetDef.errUpdate'));
      }
    } catch (error: any) {
      showGlobalNotification.error(t('zimmetDef.error'), error.message);
    }
  };

  const moveDefinition = async (id: number, direction: 'up' | 'down') => {
    const currentIndex = definitions.findIndex(d => d.id === id);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= definitions.length) return;

    const newDefinitions = [...definitions];
    const temp = newDefinitions[currentIndex];
    newDefinitions[currentIndex] = newDefinitions[newIndex];
    newDefinitions[newIndex] = temp;

    // Update sort_order
    newDefinitions.forEach((def, index) => {
      def.sort_order = index;
    });

    setDefinitions(newDefinitions);

    // Update in backend
    try {
      await Promise.all(newDefinitions.map(def =>
        fetch(`/api/assignment-definitions/${def.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({ sort_order: def.sort_order })
        })
      ));
    } catch (error) {
      console.error('Sıralama güncelleme hatası:', error);
      loadDefinitions(); // Revert on error
    }
  };

  const getFilteredDefinitions = () => {
    if (!selectedCategoryFilter) {
      return definitions;
    }
    return definitions.filter(d => d.category_id === selectedCategoryFilter);
  };

  const groupedDefinitions = categories
    .filter(cat => cat.is_active)
    .map(category => {
      const categoryDefs = definitions.filter(d => d.category_id === category.id);
      return { category, definitions: categoryDefs };
    })
    .filter(group => group.definitions.length > 0);

  const filteredDefinitions = getFilteredDefinitions();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">{t('zimmetDef.loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('zimmetDef.title')}</h1>
        {activeTab === 'definitions' ? (
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {t('zimmetDef.addDef')}
          </button>
        ) : (
          <button
            onClick={() => setShowTemplateModal(true)}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            {t('zimmetDef.addTemplate')}
          </button>
        )}
      </div>

      {/* Sekmeler */}
      <div className="flex gap-1 mb-6 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('definitions')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'definitions'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
          }`}
        >
          <Layers className="w-4 h-4" />
          {t('zimmetDef.tabDefs')}
        </button>
        <button
          onClick={() => setActiveTab('templates')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'templates'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
          }`}
        >
          <FileText className="w-4 h-4" />
          {t('zimmetDef.tabTemplates')}
        </button>
      </div>

      {/* Şablonlar Sekmesi */}
      {activeTab === 'templates' && (
        <div>
          {templatesLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-gray-500 dark:text-gray-400">{t('zimmetDef.loading')}</div>
            </div>
          ) : (
            <div className="grid gap-4">
              {templates.map(template => (
                <div key={template.id} className={`bg-white dark:bg-gray-800 rounded-lg shadow border-2 transition-all ${
                  template.is_active ? 'border-primary' : 'border-gray-200 dark:border-gray-700'
                }`}>
                  <div className="p-5 flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        template.is_active ? 'bg-primary/10' : 'bg-gray-100 dark:bg-gray-700'
                      }`}>
                        <FileText className={`w-5 h-5 ${template.is_active ? 'text-primary' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{template.name}</h3>
                          {template.is_active && (
                            <span className="flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary text-xs font-semibold rounded-full">
                              <CheckCircle className="w-3 h-3" /> {t('zimmetDef.active')}
                            </span>
                          )}
                          {template.is_default && (
                            <span className="flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">
                              <Star className="w-3 h-3" /> {t('zimmetDef.default')}
                            </span>
                          )}
                        </div>
                        {template.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{template.description}</p>
                        )}
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-400 dark:text-gray-500">
                          <span>{t('zimmetDef.key')} <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">{template.template_key}</code></span>
                          {template.file_original_name && (
                            <span>{t('zimmetDef.file')} {template.file_original_name}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {template.file_path && (
                        <button
                          onClick={() => handleDownloadTemplate(template.id)}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          {t('zimmetDef.download')}
                        </button>
                      )}
                      {!template.is_active && (
                        <button
                          onClick={() => handleActivateTemplate(template.id)}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-primary border border-primary rounded-md hover:bg-primary/5 transition-colors"
                        >
                          <CheckCircle className="w-4 h-4" />
                          {t('zimmetDef.activate')}
                        </button>
                      )}
                      {!template.is_default && (
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          {t('zimmetDef.delete')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {templates.length === 0 && (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  {t('zimmetDef.noTemplates')}
                </div>
              )}
            </div>
          )}

          {/* Yeni Şablon Modal */}
          {showTemplateModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('zimmetDef.newTemplateTitle')}</h3>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('zimmetDef.templateName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={templateForm.name}
                      onChange={e => setTemplateForm({ ...templateForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-gray-100"
                      placeholder="örn: Kurumsal Zimmet Formu v2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('zimmetDef.templateKey')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={templateForm.template_key}
                      onChange={e => setTemplateForm({ ...templateForm, template_key: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-gray-100"
                      placeholder="örn: corporate-v2"
                    />
                    <p className="text-xs text-gray-400 mt-1">{t('zimmetDef.templateKeyHint')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('zimmetDef.templateDesc')}</label>
                    <textarea
                      value={templateForm.description}
                      onChange={e => setTemplateForm({ ...templateForm, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-gray-100"
                      rows={2}
                      placeholder="Şablon hakkında kısa açıklama"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('zimmetDef.templateFile')} <span className="text-xs text-gray-400">({t('zimmetDef.templateFileLabel')})</span>
                    </label>
                    <input
                      type="file"
                      accept=".docx,.doc,.pdf"
                      onChange={e => setTemplateFile(e.target.files?.[0] || null)}
                      className="w-full text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 dark:bg-gray-700"
                    />
                    <p className="text-xs text-gray-400 mt-1">{t('zimmetDef.templateFileHint')}</p>
                  </div>
                </div>
                <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                  <button
                    onClick={() => { setShowTemplateModal(false); setTemplateForm({ name: '', description: '', template_key: '' }); setTemplateFile(null); }}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    {t('zimmetDef.cancel')}
                  </button>
                  <button
                    onClick={handleAddTemplate}
                    disabled={templateSaving}
                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors disabled:opacity-50"
                  >
                    {templateSaving ? t('zimmetDef.saving') : t('zimmetDef.add')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tanımlar Sekmesi */}
      {activeTab === 'definitions' && (
      <>

      {/* Kategori Filtreleme Butonları */}
      <div className="mb-6 flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedCategoryFilter(null)}
          className={`px-4 py-2 rounded-md transition-colors flex items-center gap-2 ${
            selectedCategoryFilter === null
              ? 'bg-primary text-white'
              : 'bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-300'
          }`}
        >
          {selectedCategoryFilter === null && <Filter className="w-4 h-4" />}
          {t('zimmetDef.all')}
        </button>
        {categories
          .filter(cat => cat.is_active)
          .map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategoryFilter(category.id)}
              className={`px-4 py-2 rounded-md transition-colors flex items-center gap-2 ${
                selectedCategoryFilter === category.id
                  ? 'bg-primary text-white'
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-300'
              }`}
              style={
                selectedCategoryFilter === category.id && category.color
                  ? { backgroundColor: category.color, color: 'white' }
                  : {}
              }
            >
              {selectedCategoryFilter === category.id && <Filter className="w-4 h-4" />}
              {category.name}
            </button>
          ))}
      </div>

      {/* Kart Tabanlı Ekran */}
      {selectedCategoryFilter ? (
        // Filtrelenmiş görünüm
        <div className="space-y-4">
          {filteredDefinitions.length > 0 ? (
            <div className="grid gap-4">
              {filteredDefinitions.map((definition, index) => (
                <div
                  key={definition.id}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg transition-shadow border border-gray-200 dark:border-gray-700"
                >
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        {editingId === definition.id ? (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {t('zimmetDef.fieldCategory')}
                              </label>
                              <select
                                value={editingDefinition.category_id || 0}
                                onChange={(e) => setEditingDefinition({ ...editingDefinition, category_id: Number(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                              >
                                <option value={0}>{t('zimmetDef.selectCategory')}</option>
                                {categories.filter(cat => cat.is_active).map((category) => (
                                  <option key={category.id} value={category.id}>
                                    {category.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {t('zimmetDef.fieldTitle')}
                              </label>
                              <input
                                type="text"
                                value={editingDefinition.title || ''}
                                onChange={(e) => setEditingDefinition({ ...editingDefinition, title: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {t('zimmetDef.fieldContent')}
                              </label>
                              <textarea
                                value={editingDefinition.content || ''}
                                onChange={(e) => setEditingDefinition({ ...editingDefinition, content: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                                rows={3}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {t('zimmetDef.fieldStatus')}
                              </label>
                              <select
                                value={editingDefinition.is_active ? 'true' : 'false'}
                                onChange={(e) => setEditingDefinition({ ...editingDefinition, is_active: e.target.value === 'true' })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                              >
                                <option value="true">{t('zimmetDef.statusActive')}</option>
                                <option value="false">{t('zimmetDef.statusInactive')}</option>
                              </select>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">#{index + 1}</span>
                              <span className="inline-block px-2 py-1 text-xs font-semibold rounded-full"
                                style={{
                                  backgroundColor: definition.category_color || '#e5e7eb',
                                  color: definition.category_color ? '#ffffff' : '#374151'
                                }}>
                                {definition.category_name}
                              </span>
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">{definition.title}</h3>
                            <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">{definition.content}</p>
                            <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                              definition.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {definition.is_active ? t('zimmetDef.statusActive') : t('zimmetDef.statusInactive')}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Sıralama ve İşlemler */}
                    <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex gap-1">
                        <button
                          onClick={() => moveDefinition(definition.id, 'up')}
                          disabled={index === 0}
                          className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ArrowUp className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => moveDefinition(definition.id, 'down')}
                          disabled={index === filteredDefinitions.length - 1}
                          className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ArrowDown className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="flex gap-2">
                        {editingId === definition.id ? (
                          <>
                            <button
                              onClick={handleSave}
                              className="flex items-center gap-1 px-3 py-2 text-sm text-green-600 hover:text-green-900"
                            >
                              <Save className="w-4 h-4" />
                              {t('zimmetDef.save')}
                            </button>
                            <button
                              onClick={handleCancel}
                              className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-100"
                            >
                              <X className="w-4 h-4" />
                              {t('zimmetDef.cancel')}
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleEdit(definition)}
                              className="flex items-center gap-1 px-3 py-2 text-sm text-primary hover:text-primary-dark"
                            >
                              <Edit className="w-4 h-4" />
                              {t('zimmetDef.edit')}
                            </button>
                            <button
                              onClick={() => handleDelete(definition.id)}
                              className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="w-4 h-4" />
                              {t('zimmetDef.delete')}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">{t('zimmetDef.noDefs')}</p>
            </div>
          )}
        </div>
      ) : (
        // Kategori bazında gruplandırılmış görünüm
        <div className="space-y-8">
          {groupedDefinitions.length > 0 ? (
            groupedDefinitions.map(group => (
              <div key={group.category.id}>
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-1 h-8 rounded-full"
                    style={{ backgroundColor: group.category.color }}
                  ></div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{group.category.name}</h2>
                  <span className="text-sm text-gray-500 dark:text-gray-400">({group.definitions.length})</span>
                </div>

                <div className="grid gap-4">
                  {group.definitions.map((definition, index) => (
                    <div
                      key={definition.id}
                      className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg transition-shadow border border-gray-200 dark:border-gray-700 cursor-pointer hover:border-gray-400"
                      onClick={() => setSelectedCategoryFilter(group.category.id)}
                    >
                      <div className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">#{index + 1}</span>
                            </div>
                            <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-2">{definition.title}</h3>
                            <p className="text-gray-600 dark:text-gray-400 text-sm mb-3 line-clamp-2">{definition.content}</p>
                            <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                              definition.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {definition.is_active ? t('zimmetDef.statusActive') : t('zimmetDef.statusInactive')}
                            </span>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(definition);
                              }}
                              className="flex items-center gap-1 px-3 py-2 text-sm text-primary hover:text-primary-dark"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(definition.id);
                              }}
                              className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">{t('zimmetDef.noDefs2')}</p>
            </div>
          )}
        </div>
      )}

      </>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('zimmetDef.newDefTitle')}</h3>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('zimmetDef.fieldCategory')} <span className="text-red-500">*</span>
                </label>
                <select
                  value={newDefinition.category_id}
                  onChange={(e) => setNewDefinition({ ...newDefinition, category_id: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value={0}>{t('zimmetDef.selectCategory')}</option>
                  {categories.filter(cat => cat.is_active).map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('zimmetDef.fieldTitle')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newDefinition.title}
                  onChange={(e) => setNewDefinition({ ...newDefinition, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder={t('zimmetDef.titlePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('zimmetDef.fieldContent')} <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newDefinition.content}
                  onChange={(e) => setNewDefinition({ ...newDefinition, content: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  rows={4}
                  placeholder={t('zimmetDef.contentPlaceholder')}
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                {t('zimmetDef.cancel')}
              </button>
              <button
                onClick={handleAdd}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
              >
                {t('zimmetDef.add')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentDefinitionsPage;
